<script setup lang="ts">
import type { PrimitiveProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import type { ButtonVariants } from "."
import { Loader2Icon } from "lucide-vue-next"
import { Primitive } from "reka-ui"
import { cn } from "@/lib/utils"
import { buttonVariants } from "."

interface Props extends PrimitiveProps {
  variant?: ButtonVariants["variant"]
  size?: ButtonVariants["size"]
  class?: HTMLAttributes["class"]
  loading?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  as: "button",
  variant: "default",
  size: "default",
  class: undefined,
  loading: false,
  disabled: false,
})
</script>

<template>
  <Primitive
    :as="as"
    :as-child="asChild"
    :disabled="disabled || loading"
    :class="cn(buttonVariants({ variant, size }), loading && 'relative', props.class)"
  >
    <!-- asChild merges props onto the slot's root element, so it must stay the
         single root child — no loading wrapper there (those are never loading). -->
    <slot v-if="asChild" />
    <template v-else>
      <!-- While loading, keep the label in the DOM (opacity-0) so the button keeps its
           intrinsic width under the overlaid spinner. When not loading, render the slot
           directly so the button's own layout (e.g. `justify-between` on combobox triggers)
           governs — an extra centering wrapper would bunch label + chevron together. -->
      <span v-if="loading" class="inline-flex items-center justify-center gap-2 opacity-0">
        <slot />
      </span>
      <slot v-else />
      <Loader2Icon
        v-if="loading"
        class="absolute left-1/2 top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 animate-spin text-current"
      />
    </template>
  </Primitive>
</template>
